import { useEffect, useState } from 'react';
import api from "../../utils/axiosInstance";
import { useParams } from 'react-router-dom';
import AdminSideMenu from '../../components/AdminSideMenu';

const AssignDriver = () => {
    const { carrierId } = useParams();
    const [drivers, setDrivers] = useState([]);
    const [selectedDrivers, setSelectedDrivers] = useState([]);
    const [carrierName, setCarrierName] = useState('');
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchDriversAndCarrier();
    }, [carrierId]);

    const fetchDriversAndCarrier = async () => {
        try {
            setLoading(true);
            const [driverRes, carrierRes] = await Promise.all([
                api.get('/drivers/unassigned'),
                api.get(`/carriers/${carrierId}`)
            ]);

            setDrivers(driverRes.data || []);
            setCarrierName(carrierRes.data?.companyName || carrierId);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (driverId) => {
        setSelectedDrivers(prev =>
            prev.includes(driverId)
                ? prev.filter(id => id !== driverId)
                : [...prev, driverId]
        );
    };

    const handleAssignSelected = async () => {
        if (selectedDrivers.length === 0) return;

        try {
            setAssigning(true);

            const res = await api.post('/driver-assignments/bulk-assign', {
                carrierId,
                driverIds: selectedDrivers
            });

            if (res.data.success) {
                setSuccessMessage(`✅ ${res.data.assignedCount} driver(s) assigned`);
                setSelectedDrivers([]);

                await fetchDriversAndCarrier();

                setTimeout(() => setSuccessMessage(''), 1000);
            } else {
                alert(res.data.message || 'Assignment failed');
            }
        } catch (err) {
            console.error('Assignment error:', err.response?.data || err.message);
            alert(err.response?.data?.message || 'Server error during assignment');
        } finally {
            setAssigning(false);
        }
    };

    return (
        <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
            <AdminSideMenu />
            <div className="w-full md:ml-64 p-4 sm:p-6 lg:p-8">
                <div className="p-0 sm:p-6">
                    <h2 className="text-2xl font-bold mb-4">Assign Drivers to {carrierName}</h2>
                    {successMessage && (
                        <div className="mb-4 text-green-700 bg-green-100 border border-green-400 px-4 py-2 rounded">
                            {successMessage}
                        </div>
                    )}

                    {loading ? (
                        <p>Loading drivers...</p>
                    ) : (
                        <div className="border border-gray-300 bg-white rounded-md">
                            <div className="max-h-[500px] overflow-y-auto">
                                {/* Responsive horizontal scroll for table */}
                                <div className="w-full overflow-x-auto">
                                    <table className="min-w-[700px] min-w-full table-fixed border-collapse">
                                        <thead className="sticky top-0 z-20">
                                            <tr>
                                                <th className="py-2 px-4 border w-48 bg-black text-white">Name</th>
                                                <th className="py-2 px-4 border w-32 bg-black text-white">Experience</th>
                                                <th className="py-2 px-4 border w-32 bg-black text-white">CDL</th>
                                                <th className="py-2 px-4 border w-32 bg-black text-white">Trailer</th>
                                                <th className="py-2 px-4 border w-64 bg-black text-white">Address</th>
                                                <th className="py-2 px-4 border w-56 bg-black text-white">
                                                    <button
                                                        disabled={selectedDrivers.length === 0 || assigning}
                                                        onClick={handleAssignSelected}
                                                        className={`bg-blue-600 text-white px-4 py-1 rounded font-semibold hover:bg-blue-700 transition text-sm ${selectedDrivers.length === 0 || assigning ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    >
                                                        {assigning ? 'Assigning...' : 'Assign Selected'}
                                                    </button>
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-gray-200">
                                            {drivers.map((driver) => (
                                                <tr key={driver._id} className="hover:bg-gray-50">
                                                    <td className="py-2 px-4 border w-48">{driver.name}</td>
                                                    <td className="py-2 px-4 border w-32">{driver.experience}</td>
                                                    <td className="py-2 px-4 border w-32">{driver.cdl}</td>
                                                    <td className="py-2 px-4 border w-32">{Array.isArray(driver.trailer) ? driver.trailer.join(', ') : driver.trailer}</td>
                                                    <td className="py-2 px-4 border w-64">
                                                        {driver.address}, {driver.city}, {driver.state}
                                                    </td>
                                                    <td className="py-2 px-4 border w-56 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedDrivers.includes(driver._id)}
                                                            onChange={() => handleCheckboxChange(driver._id)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AssignDriver;
